import { TDeviceType } from '@cian/ui-kit/types';

import {
  dealTypeFromJsonQuery,
  offerTypeFromJsonQuery,
  FDealType,
  FOfferType,
  TJsonQuery,
} from '../../../packages/JsonQuery';
import { EPageType, TPageType } from 'shared/types/page';

export type TUxfbPlatform = 'desktop' | 'mobweb';
export type TUxfbViewMode = 'list' | 'map';
export type TUxfbDealType = 'sale' | 'rent';
export type TUxfbOfferType = 'flat' | 'suburban' | 'commercial';
export type TUxfbIsDaily = 'yes' | 'no';
export type TUxfbListingType = 'new' | 'resale' | 'resale_and_new';

export const getPlatform = (deviceType: TDeviceType): TUxfbPlatform =>
  deviceType === 'desktop' ? 'desktop' : 'mobweb';

/** Режим выдачи: в стейте признака нет, поэтому определяем по URL — карта живёт под /map. */
export const getViewMode = (pathname: string): TUxfbViewMode => (pathname.startsWith('/map') ? 'map' : 'list');

export const getDealType = (jsonQuery: TJsonQuery): TUxfbDealType =>
  dealTypeFromJsonQuery(jsonQuery) === FDealType.Sale ? 'sale' : 'rent';

export const getOfferType = (jsonQuery: TJsonQuery): TUxfbOfferType => {
  const offerType = offerTypeFromJsonQuery(jsonQuery);

  if ((offerType & (FOfferType.Commercial | FOfferType.Garage)) !== 0) {
    return 'commercial';
  }

  if ((offerType & FOfferType.Suburban) !== 0) {
    return 'suburban';
  }

  return 'flat';
};

/**
 * Посуточная ли выдача: страница посуточной аренды либо посуточный тип сделки в jsonQuery.
 * Неуточнённую аренду (FDealType.Rent без for_day) посуткой не считаем — как в эталоне frontend-serp.
 */
export const getIsDaily = (jsonQuery: TJsonQuery, pageType?: TPageType): TUxfbIsDaily =>
  pageType === EPageType.SEARCH_RENT_DAILY || dealTypeFromJsonQuery(jsonQuery) === FDealType.RentDaily ? 'yes' : 'no';

/**
 * Тип недвижимости. На событии фильтров атрибут называется `listing_type` (на событии выдачи — `property_type`,
 * значение одинаковое). Для не-flat отдаём `resale_and_new` — по аналогии с аппками.
 */
export const getListingType = (jsonQuery: TJsonQuery): TUxfbListingType => {
  const offerType = offerTypeFromJsonQuery(jsonQuery);
  const isNew = (offerType & FOfferType.FlatNew) !== 0;
  const isOld = (offerType & FOfferType.FlatOld) !== 0;

  if (isNew && isOld) {
    return 'resale_and_new';
  }

  if (isNew) {
    return 'new';
  }

  if (isOld) {
    return 'resale';
  }

  return 'resale_and_new';
};

/**
 * Имя применённого фильтра. В точке отправки (санк applyFilters) конкретный фильтр надёжно не различить:
 * при авто-применении (applyFilterAfterChange) `lastAppliedFilter` не успевает обновиться до санка.
 * Пока по договорённости шлём `other` для всех — допустимый фолбэк для вторички.
 */
export const getFilterName = (): string => 'other';

export interface IGetSerpFilterAppliedAttributesParams {
  jsonQuery: TJsonQuery;
  deviceType: TDeviceType;
  viewMode: TUxfbViewMode;
  pageType?: TPageType;
}

/** Атрибуты события `serp_filter_applied` (применение фильтра на выдаче). */
export const getSerpFilterAppliedAttributes = ({
  jsonQuery,
  deviceType,
  viewMode,
  pageType,
}: IGetSerpFilterAppliedAttributesParams): Record<string, string> => ({
  platform: getPlatform(deviceType),
  view_mode: viewMode,
  listing_type: getListingType(jsonQuery),
  filter_name: getFilterName(),
  deal_type: getDealType(jsonQuery),
  offer_type: getOfferType(jsonQuery),
  is_daily: getIsDaily(jsonQuery, pageType),
});
